import { useEffect, useMemo, useRef } from "react";
import { Box, Card, Skeleton, Spinner, Text } from "@salestrak/ui";

import type { NotificationRow as InboxStandardFragment } from "#app/lib/data";
import type { InboxDateGroup } from "#app/hooks/inbox/use-inbox-date-groups";
import { DatePill, formatDateKey, useDateJump } from "@salestrak/ui";
import { InboxNotificationItem, type InboxTrigger } from "./inbox-notification-item";
import { InboxEmptyState } from "./inbox-empty-state";

// Group header rhythm: 16px above + ~20px line + 12px below. useDateJump uses
// it both as the sticky-activation buffer and the floating-pill hide window.
const GROUP_HEADER_HEIGHT = 48;

// Initial-load placeholder mirroring the loaded anatomy — date label, then a
// bundle card of 44px rows (stage pie + title in the fixed 30% column, kind
// glyph + summary filling, date at the right end). Fixed per-row width cycles
// keep the shimmer organic without any per-render randomness.
const SKELETON_GROUPS = [4, 3] as const;

function InboxListSkeleton() {
  return (
    <Box fill="flex" overflow="hidden">
      <Box direction="column" paddingX="md" paddingBottom="md" fill="width">
        {SKELETON_GROUPS.map((rowCount, groupIndex) => (
          <Box key={groupIndex} direction="column">
            <Box paddingTop="md" paddingBottom="base" paddingLeft="md">
              {/* The date label's line box is ~20px; the bar sits at text
                  height so the card below lands on the loaded rhythm. */}
              <Box direction="row" align="center" height={20}>
                <Skeleton width={72} height={12} />
              </Box>
            </Box>
            <Card radius="lg" surface="background">
              {Array.from({ length: rowCount }, (_, index) => (
                <Box
                  key={index}
                  direction="row"
                  align="center"
                  height={44}
                  paddingLeft="md"
                  paddingRight="base"
                  border={index < rowCount - 1 ? "bottom" : undefined}
                >
                  <Box
                    direction="row"
                    align="center"
                    gap="sm"
                    width="30%"
                    shrink={false}
                    paddingRight="lg"
                  >
                    <Skeleton circle width={14} />
                    <Skeleton height={12} vary={index} width={96} />
                  </Box>
                  <Box direction="row" align="center" gap="sm" flex={1} minWidth={0}>
                    <Skeleton circle width={14} />
                    <Skeleton height={12} vary={index + 1} width="55%" />
                  </Box>
                  <Skeleton width={48} height={10} />
                </Box>
              ))}
            </Card>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

interface InboxListProps {
  groups: InboxDateGroup[];
  /** All days with items, sorted descending — enables the jump-to-date popover. */
  dateKeys: string[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => Promise<unknown> | void;
  onItemClick: (item: InboxStandardFragment) => void;
  onClear: (item: InboxStandardFragment, via: InboxTrigger) => void;
  onUnclear: (item: InboxStandardFragment, via: InboxTrigger) => void;
  onToggleRead: (item: InboxStandardFragment, via: InboxTrigger) => void;
  onOpenInNewTab: (item: InboxStandardFragment) => void;
  onCopyLink: (item: InboxStandardFragment) => void;
  activeTab: "all" | "cleared";
  /** A kind filter is applied — swaps the empty state's copy. */
  filtered?: boolean;
  /** Clears the applied filter from the empty state's CTA. */
  onClearFilter?: () => void;
}

export function InboxList({
  groups,
  dateKeys,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
  onItemClick,
  onClear,
  onUnclear,
  onToggleRead,
  onOpenInNewTab,
  onCopyLink,
  activeTab,
  filtered,
  onClearFilter,
}: InboxListProps) {
  const isCleared = activeTab === "cleared";
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Jump-to-date + floating sticky-date header, shared with the activity feed.
  const dateJumpGroups = useMemo(
    () => groups.map((g) => ({ dateKey: g.dateKey, dateLabel: g.dateLabel })),
    [groups],
  );
  const { scrollToDate, stickyDateLabel, isJumpingRef } = useDateJump({
    scrollRef,
    groups: dateJumpGroups,
    hasMore,
    loadMore: () => onLoadMore() ?? Promise.resolve(),
    separatorHeight: GROUP_HEADER_HEIGHT,
  });

  // Infinite scroll: observe a sentinel element near the bottom of the list.
  // When it enters the visible area (accounting for the scroller's overflow
  // clip), trigger the next page load. Skips while a jump is paginating so the
  // two loaders don't race.
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    if (!hasMore || loadingMore) return;

    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && !isJumpingRef.current) {
          onLoadMore();
        }
      },
      { rootMargin: "200px" },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, onLoadMore, isJumpingRef]);

  if (loading) {
    return <InboxListSkeleton />;
  }

  if (groups.length === 0) {
    return (
      <Box fill="flex" align="center" justify="center">
        <InboxEmptyState
          activeTab={activeTab}
          filtered={filtered}
          onClearFilter={onClearFilter}
        />
      </Box>
    );
  }

  return (
    <Box direction="column" flex={1} minHeight={0} position="relative">
      {/* Floating date pill — appears when a group header scrolls past the
          top. The wrapper spans the column so the pill centers, but only the
          pill itself takes the pointer. */}
      {stickyDateLabel && (
        <Box
          pointerEvents="none" position="absolute"
          top={8}
          left={0}
          right={0}
          zIndex={20}
          align="center"
        >
          <Box pointerEvents="auto">
            <DatePill
              label={stickyDateLabel}
              scrollToDate={scrollToDate}
              dateKeys={dateKeys}
            />
          </Box>
        </Box>
      )}

      {/* Plain overflow container, not a ScrollArea: useDateJump's scroll
          target and the pagination observer both read this element directly.
          The inner column carries the header's 16px gutter (paddingX md) so the
          group labels and bundles line up with the breadcrumb + filter row. */}
      <Box ref={scrollRef} fill="flex" overflow="auto">
        <Box direction="column" fill="width">
          <Box
            direction="column"
            paddingX="md"
            paddingBottom="md"
            fill="width"
          >
            {groups.map((group) => {
              // Day anchors for jump-to-date: the first row of each day inside
              // a month bucket carries `date-group-${dayKey}`. Skipped when
              // the bucket key IS the day key (Today/Yesterday) — the group
              // wrapper already owns that id.
              const seenDays = new Set<string>();
              return (
                <Box
                  key={group.dateKey}
                  direction="column"
                  id={`date-group-${group.dateKey}`}
                >
                  {/* paddingLeft md lands the label on the row's leading rail
                      (the row's own paddingLeft), so it lines up with the
                      item glyphs below it. */}
                  <Box paddingTop="md" paddingBottom="base" paddingLeft="md">
                    <Text size="sm" weight="medium">
                      {group.dateLabel}
                    </Text>
                  </Box>
                  <Card radius="lg" surface="background">
                    {group.items.map((item, index) => {
                      const dayKey = formatDateKey(
                        new Date(item.creationDate),
                      );
                      const isDayAnchor =
                        dayKey !== group.dateKey && !seenDays.has(dayKey);
                      seenDays.add(dayKey);
                      return (
                        <InboxNotificationItem
                          key={`${item.inboxId}-${index}`}
                          {...(isDayAnchor
                            ? { id: `date-group-${dayKey}` }
                            : {})}
                          item={item}
                          onClick={onItemClick}
                          onClear={onClear}
                          onUnclear={onUnclear}
                          onToggleRead={onToggleRead}
                          onOpenInNewTab={onOpenInNewTab}
                          onCopyLink={onCopyLink}
                          isCleared={isCleared}
                          divider={index < group.items.length - 1}
                        />
                      );
                    })}
                  </Card>
                </Box>
              );
            })}

            {/* Infinite scroll sentinel + loading indicator */}
            {hasMore && (
              <Box ref={sentinelRef} direction="column" paddingY="md" align="center">
                {loadingMore && <Spinner size="xl" />}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
